import React, { useState, useEffect, useCallback, useRef } from 'react';
import MaintenanceScreen from './MaintenanceScreen';
import { fetchSystemStatus, SERVICE_PAUSED_EVENT } from '../services/systemStatusService';
import { ROLES } from '../utils/rbac';

const POLL_INTERVAL_MS = 45000;

/**
 * Wraps the whole PMS. When a super admin has paused the PMS, every other
 * role sees the maintenance screen instead of the app.
 *
 * SUPERADMIN sessions pass straight through — that is what makes the switch
 * reversible from inside the PMS — and instead get a banner reminding them
 * the platform is currently paused for everyone else.
 */
function SystemStatusGate({ user, onLogout, children }) {
  const [maintenance, setMaintenance] = useState(null);
  const [bookingEngine, setBookingEngine] = useState(null);
  const [contact, setContact] = useState(null);
  const [checking, setChecking] = useState(false);
  const mounted = useRef(true);

  const isSuperAdmin = user?.role === ROLES.SUPERADMIN;

  const check = useCallback(async ({ showSpinner = false } = {}) => {
    if (showSpinner) setChecking(true);
    const status = await fetchSystemStatus();
    if (!mounted.current) return;

    if (status) {
      setMaintenance(status.maintenance?.active ? status.maintenance : null);
      setBookingEngine(status.bookingEngine?.active ? status.bookingEngine : null);
      setContact(status.contact || null);
    }
    // status === null means the check itself failed; keep the current view
    // rather than locking staff out over a flaky connection.
    if (showSpinner) setChecking(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    check();

    const interval = setInterval(check, POLL_INTERVAL_MS);

    const onPaused = (event) => {
      const detail = event?.detail;
      if (detail?.surface && detail.surface !== 'PMS') return;
      if (detail) {
        setMaintenance(detail);
        setContact(detail.contact || null);
      } else {
        check();
      }
    };

    window.addEventListener(SERVICE_PAUSED_EVENT, onPaused);

    return () => {
      mounted.current = false;
      clearInterval(interval);
      window.removeEventListener(SERVICE_PAUSED_EVENT, onPaused);
    };
  }, [check]);

  // Re-check as soon as somebody signs in, so a staff member cannot land on a
  // half-loaded dashboard between polls.
  useEffect(() => {
    if (user) check();
  }, [user, check]);

  if (maintenance && !isSuperAdmin) {
    return (
      <MaintenanceScreen
        maintenance={maintenance}
        contact={contact}
        user={user}
        retrying={checking}
        onRetry={() => check({ showSpinner: true })}
        onLogout={onLogout}
      />
    );
  }

  const pausedSurfaces = [];
  if (maintenance) pausedSurfaces.push('PMS');
  if (bookingEngine) pausedSurfaces.push('Booking Engine');

  return (
    <>
      {isSuperAdmin && pausedSurfaces.length > 0 && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2000,
            backgroundColor: '#772D21',
            color: '#ffffff',
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          Kill switch is ACTIVE — {pausedSurfaces.join(' & ')} paused for all other users. You have super-admin
          access. Open <strong>System Control</strong> to restore service.
        </div>
      )}
      {children}
    </>
  );
}

export default SystemStatusGate;
